import React, { useState, useMemo } from "react";

const PayrollTable = ({ columns = [], rows = [] }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const validColumns = Array.isArray(columns) ? columns : [];
  const validRows = Array.isArray(rows) ? rows : [];

  const minTableWidth = useMemo(() => {
    const totalExplicitWidth = validColumns.reduce((acc, col) => {
      const w = typeof col.width === "number" ? col.width : parseInt(col.width, 10);
      return acc + (isNaN(w) ? 0 : w);
    }, 0);
    return totalExplicitWidth > 500 ? `${totalExplicitWidth}px` : "100%";
  }, [validColumns]);

  if (!Array.isArray(columns) || !Array.isArray(rows)) {
    return <div className="p-4 text-red-500">Error: Invalid table data</div>;
  }

  const totalPages = Math.ceil(validRows.length / rowsPerPage);
  const currentRows = validRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getAlignmentClass = (col) => {
    if (col.align === "left") return "text-left";
    if (col.align === "right") return "text-right";
    if (col.align === "center") return "text-center";
    return "text-center";
  };

  return (
    <div className="w-full overflow-x-auto rounded-t-[20px] shadow-lg bg-white">
      <div style={{ minWidth: minTableWidth, width: "100%" }}>
        <table className="w-full border-collapse bg-white table-fixed">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={col.field || index}
                  className={`bg-[#133a69] text-white font-bold py-3.5 px-4 border-b border-gray-200 text-xs md:text-sm tracking-wide ${getAlignmentClass(col)}`}
                  style={{ width: col.width ? (typeof col.width === "number" ? `${col.width}px` : col.width) : "auto" }}
                >
                  {col.headerName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              currentRows.map((row, rowIndex) => (
                <tr key={row.id || rowIndex} className="hover:bg-gray-50 transition-colors">
                  {columns.map((col, colIndex) => (
                    <td
                      key={`${row.id || rowIndex}-${col.field || colIndex}`}
                      className={`py-3.5 px-4 border-b border-gray-200 text-xs md:text-sm text-gray-800 font-medium whitespace-nowrap ${getAlignmentClass(col)}`}
                      style={{ width: col.width ? (typeof col.width === "number" ? `${col.width}px` : col.width) : "auto" }}
                    >
                      {row[col.field]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-6 text-center text-gray-500 font-medium"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {rows.length > rowsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex justify-between flex-1 sm:hidden">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 ${page === 0 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 ${page >= totalPages - 1 ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{page * rowsPerPage + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min((page + 1) * rowsPerPage, rows.length)}
                  </span>{" "}
                  of <span className="font-medium">{rows.length}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="inline-flex -space-x-px rounded-md shadow-sm"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 ${page === 0 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    <span>Previous</span>
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${page === i
                        ? "z-10 bg-blue-50 border-blue-500 text-blue-600 font-bold"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 ${page >= totalPages - 1 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    <span>Next</span>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollTable;